import * as React from "react";
import { useI18n } from "./I18nProvider";
import { fetchLocalizationModule } from "../api/localization";
import { resolveContentString } from "./contentText";

type CacheEntry = {
  definitions: Record<string, string>;
  fallbackDefinitions: Record<string, string>;
};

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<CacheEntry>>();
const listeners = new Set<() => void>();

async function loadCertificationDefs(language: string): Promise<CacheEntry> {
  const lang = language || "en";
  const cached = cache.get(lang);
  if (cached) return cached;

  const existing = inflight.get(lang);
  if (existing) return existing;

  const promise = (async () => {
    const primary = await fetchLocalizationModule("certification", lang);
    const primaryDefs = primary.definitions ?? {};
    let fallbackDefinitions = primaryDefs;
    if (lang !== "en") {
      const fallback = await fetchLocalizationModule("certification", "en");
      fallbackDefinitions = fallback.definitions ?? {};
    }
    const entry = { definitions: primaryDefs, fallbackDefinitions };
    cache.set(lang, entry);
    return entry;
  })().finally(() => {
    inflight.delete(lang);
  });

  inflight.set(lang, promise);
  return promise;
}

/**
 * Invalidate cached certification defs (e.g. after saving training translations).
 * Soft: keeps last-known entries in place until the refetch completes so
 * `resolve()` does not briefly fall back to raw content keys (blank flash).
 */
export function invalidateCertificationContentDefinitionsCache(language?: string) {
  if (language) {
    // Mark for reload without clearing displayable defs mid-render.
    cache.delete(language);
  } else {
    cache.clear();
  }
  listeners.forEach((listener) => listener());
}

/**
 * Loads the `certification` localization module for the active language
 * (and default/en as fallback) so admin surfaces outside the certification
 * app can resolve `certification.content.*` keys to display strings.
 */
export function useCertificationContentDefinitions() {
  const { language } = useI18n();
  const [definitions, setDefinitions] = React.useState<Record<string, string>>(
    () => cache.get(language || "en")?.definitions ?? {},
  );
  const [fallbackDefinitions, setFallbackDefinitions] = React.useState<
    Record<string, string>
  >(() => cache.get(language || "en")?.fallbackDefinitions ?? {});
  const [ready, setReady] = React.useState(() => cache.has(language || "en"));
  const [tick, setTick] = React.useState(0);

  React.useEffect(() => {
    const onInvalidate = () => setTick((n) => n + 1);
    listeners.add(onInvalidate);
    return () => {
      listeners.delete(onInvalidate);
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    const lang = language || "en";
    // Soft refresh: keep last-known definitions/ready while cache is refetched
    // after invalidate, so strip titles don't flash raw content keys.
    const isInitialMiss = !cache.has(lang) && Object.keys(definitions).length === 0;
    if (isInitialMiss) setReady(false);

    loadCertificationDefs(lang)
      .then((entry) => {
        if (cancelled) return;
        setDefinitions(entry.definitions);
        setFallbackDefinitions(entry.fallbackDefinitions);
        setReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        if (isInitialMiss) {
          setDefinitions({});
          setFallbackDefinitions({});
        }
        setReady(true);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keep prior defs during soft invalidate
  }, [language, tick]);

  const resolve = React.useCallback(
    (value: string | null | undefined) =>
      resolveContentString(value, definitions, fallbackDefinitions),
    [definitions, fallbackDefinitions],
  );

  return { resolve, definitions, fallbackDefinitions, ready, language };
}
